import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import api from '../services/api';
import { showToast } from '../components/UI/Toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const Reports: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Log Sheet Details Report State
  const [logSheetDetails, setLogSheetDetails] = useState<any>(null);
  const [selectedRigId, setSelectedRigId] = useState<number | null>(null);
  const [rigs, setRigs] = useState<any[]>([]);

  // Date filters
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const clearFilterTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (user && user.role !== 'ADMIN') {
      showToast.error('You do not have permission to access reports');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    return () => {
      if (clearFilterTimerRef.current) clearTimeout(clearFilterTimerRef.current);
    };
  }, []);

  useEffect(() => {
    // Set default dates to current month if not already set
    if (!startDate && !endDate) {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(firstDay);
      setEndDate(now);
    }
    fetchRigs();
    fetchLogSheetDetailsReport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDateParams = () => {
    const params: any = {};
    if (startDate) {
      params.startDate = startDate.toISOString().split('T')[0];
    }
    if (endDate) {
      params.endDate = endDate.toISOString().split('T')[0];
    }
    return params;
  };

  const fetchRigs = async () => {
    try {
      const response = await api.get('/rigs', { params: { limit: 1000 } }); // Get all rigs
      setRigs(response.data.data || []);
    } catch (error) {
      console.error('Error fetching rigs:', error);
    }
  };

  const fetchLogSheetDetailsReport = async () => {
    try {
      setLoading(true);
      const params: any = getDateParams();
      if (selectedRigId) {
        params.rigId = selectedRigId;
      }
      const response = await api.get('/reports/logsheet-details', { params });
      setLogSheetDetails(response.data);
    } catch (error) {
      console.error('Error fetching log sheet details report:', error);
      showToast.error('Failed to fetch log sheet details report');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateFilter = () => {
    fetchLogSheetDetailsReport();
  };

  const handleClearDateFilter = () => {
    if (clearFilterTimerRef.current) clearTimeout(clearFilterTimerRef.current);
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    setStartDate(firstDay);
    setEndDate(now);
    setSelectedRigId(null);
    clearFilterTimerRef.current = setTimeout(() => {
      clearFilterTimerRef.current = null;
      fetchLogSheetDetailsReport();
    }, 100);
  };

  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      const params: any = getDateParams();
      if (selectedRigId) {
        params.rigId = selectedRigId;
      }

      const response = await api.get('/reports/logsheet-details/export', {
        params,
        responseType: 'blob'
      });

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with date range
      const fromDate = startDate ? startDate.toLocaleDateString('en-GB').replace(/\//g, '-') : 'all';
      const toDate = endDate ? endDate.toLocaleDateString('en-GB').replace(/\//g, '-') : 'all';
      link.download = `LogSheet_Details_${fromDate}_to_${toDate}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showToast.error('Failed to export to Excel');
    } finally {
      setLoading(false);
    }
  };

  if (user && user.role !== 'ADMIN') {
    return null;
  }

  return (
    <MainLayout>
      <div className="space-y-6 overflow-y-auto h-full">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Log Sheet Details Report</h1>
            <p className="text-sm text-gray-600 mt-1">Detailed rig-wise log sheet analysis and metrics</p>
          </div>
          <button
            onClick={handleExportToExcel}
            disabled={loading || !logSheetDetails}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Export to Excel
          </button>
        </div>

        {/* Date Range Filter */}
        {(
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date | null) => setStartDate(date)}
                  selectsStart
                  startDate={startDate || undefined}
                  endDate={endDate || undefined}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select start date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  wrapperClassName="w-full"
                  isClearable
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date: Date | null) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate || undefined}
                  endDate={endDate || undefined}
                  minDate={startDate || undefined}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select end date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  wrapperClassName="w-full"
                  isClearable
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Rig No</label>
                <select
                  value={selectedRigId || ''}
                  onChange={(e) => setSelectedRigId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Rigs</option>
                  {rigs.map((rig) => (
                    <option key={rig.id} value={rig.id}>
                      {rig.rig_no}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={handleApplyDateFilter}
                  className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap"
                >
                  Apply Filter
                </button>
                <button
                  onClick={handleClearDateFilter}
                  className="flex-1 sm:flex-none px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Report Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-600">Loading report...</div>
          </div>
        ) : (
          <LogSheetDetailsTab data={logSheetDetails} />
        )}
      </div>
    </MainLayout>
  );
};

// Log Sheet Details Tab Component
const LogSheetDetailsTab: React.FC<{ data: any }> = ({ data }) => {
  const [expandedRigs, setExpandedRigs] = React.useState<Set<string>>(new Set());

  if (!data) return null;

  const toggleRig = (rigNo: string) => {
    const newExpanded = new Set(expandedRigs);
    if (newExpanded.has(rigNo)) {
      newExpanded.delete(rigNo);
    } else {
      newExpanded.add(rigNo);
    }
    setExpandedRigs(newExpanded);
  };

  const expandAll = () => {
    const allRigs = new Set<string>(data.rigGroups.map((group: any) => String(group.rig_no)));
    setExpandedRigs(allRigs);
  };

  const collapseAll = () => {
    setExpandedRigs(new Set());
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Log Sheet Details Summary</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="text-sm text-gray-600 mb-1">Total Log Sheets</div>
            <div className="text-2xl sm:text-3xl font-bold text-blue-600">{data.summary.total_log_sheets}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="text-sm text-gray-600 mb-1">Total Depth (ft)</div>
            <div className="text-2xl sm:text-3xl font-bold text-green-600">{data.summary.total_depth.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="text-sm text-gray-600 mb-1">Total Bill Amount</div>
            <div className="text-2xl sm:text-3xl font-bold text-purple-600">₹{data.summary.total_bill_amount.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="text-sm text-gray-600 mb-1">Total RPM</div>
            <div className="text-2xl sm:text-3xl font-bold text-orange-600">{data.summary.total_rpm.toFixed(1)}</div>
          </div>
        </div>
      </div>

      {/* Rig-wise Grouped Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-md font-semibold text-gray-800">Detailed Log Sheet Report (Rig-wise)</h4>
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Collapse All
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Log No</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Customer</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Street</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Depth</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">PVC 1</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">PVC 2</th>
                {/* Dynamic PVC inch columns */}
                {data.uniqueInches && data.uniqueInches.map((inch: number) => (
                  <th key={inch} className="px-3 py-2 text-right text-xs font-medium text-gray-500">{inch}" Total</th>
                ))}
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Yield</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">RPM End</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">RPM Start</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">RPM</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Bill Amount</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Rec Amount</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Rec Date</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Pipe Cost</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Bill-Pipe</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Depth/RPM</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amt/RPM</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {data.rigGroups.map((rigGroup: any) => (
                <React.Fragment key={rigGroup.rig_no}>
                  {/* Rig Header Row */}
                  <tr className="bg-blue-600 text-white cursor-pointer hover:bg-blue-700" onClick={() => toggleRig(rigGroup.rig_no)}>
                    <td colSpan={4} className="px-3 py-3 text-sm font-semibold">
                      <div className="flex items-center gap-2">
                        <span>{expandedRigs.has(rigGroup.rig_no) ? '▼' : '▶'}</span>
                        <span>Rig No: {rigGroup.rig_no}</span>
                        <span className="ml-4">({rigGroup.totals.total_log_sheets} sheets)</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm text-right">{rigGroup.totals.total_depth}</td>
                    <td className="px-3 py-3 text-sm"></td>
                    <td className="px-3 py-3 text-sm"></td>
                    {/* Dynamic PVC inch totals */}
                    {data.uniqueInches && data.uniqueInches.map((inch: number) => (
                      <td key={inch} className="px-3 py-3 text-sm text-right">
                        {rigGroup.totals[`pvc_${inch}_total`] > 0 ? `₹${rigGroup.totals[`pvc_${inch}_total`].toLocaleString()}` : '-'}
                      </td>
                    ))}
                    <td className="px-3 py-3 text-sm"></td>
                    <td className="px-3 py-3 text-sm"></td>
                    <td className="px-3 py-3 text-sm"></td>
                    <td className="px-3 py-3 text-sm text-right">{rigGroup.totals.total_rpm.toFixed(1)}</td>
                    <td className="px-3 py-3 text-sm text-right">₹{rigGroup.totals.total_bill_amount.toLocaleString()}</td>
                    <td className="px-3 py-3 text-sm text-right">₹{rigGroup.totals.total_received_amount.toLocaleString()}</td>
                    <td className="px-3 py-3 text-sm"></td>
                    <td className="px-3 py-3 text-sm text-right">₹{rigGroup.totals.total_pipe_cost.toLocaleString()}</td>
                    <td className="px-3 py-3 text-sm"></td>
                    <td className="px-3 py-3 text-sm"></td>
                    <td className="px-3 py-3 text-sm"></td>
                  </tr>

                  {/* Rig Detail Rows */}
                  {expandedRigs.has(rigGroup.rig_no) && rigGroup.logSheets.map((item: any) => (
                    <tr key={item.log_sheet_no} className={item.is_cancelled ? 'bg-red-50' : 'hover:bg-gray-50'}>
                      <td className="px-3 py-2 text-sm text-gray-900 pl-8">{item.log_sheet_no}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">
                        {item.log_date ? new Date(item.log_date).toLocaleDateString('en-GB') : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">{item.customer_name}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{item.street}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-900">{item.depth}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{item.pvc_1_display || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{item.pvc_2_display || '-'}</td>
                      {/* Dynamic PVC inch totals */}
                      {data.uniqueInches && data.uniqueInches.map((inch: number) => (
                        <td key={inch} className="px-3 py-2 text-sm text-right text-gray-900">
                          {item[`pvc_${inch}_total`] > 0 ? `₹${item[`pvc_${inch}_total`].toLocaleString()}` : '-'}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-sm text-gray-600">{item.yield}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-600">{item.rpm_end}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-600">{item.rpm_start}</td>
                      <td className="px-3 py-2 text-sm text-right font-medium text-blue-600">{item.rpm.toFixed(1)}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-900">₹{item.bill_amount.toLocaleString()}</td>
                      <td className="px-3 py-2 text-sm text-right text-green-600">₹{item.received_amount.toLocaleString()}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">
                        {item.received_date ? new Date(item.received_date).toLocaleDateString('en-GB') : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-orange-600">₹{item.pipe_cost.toLocaleString()}</td>
                      <td className="px-3 py-2 text-sm text-right text-purple-600">₹{item.bill_amt_minus_pipe.toLocaleString()}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-600">{item.depth_per_rpm}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-600">{item.net_amt_per_rpm.toLocaleString()}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            {/* Totals Row */}
            <tfoot className="bg-gray-100 font-semibold">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-sm text-right text-gray-900">TOTAL</td>
                <td className="px-3 py-2 text-sm text-right text-gray-900">{data.summary.total_depth.toLocaleString()}</td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2"></td>
                {/* Dynamic PVC inch totals */}
                {data.uniqueInches && data.uniqueInches.map((inch: number) => (
                  <td key={inch} className="px-3 py-2 text-sm text-right text-gray-900">
                    {data.summary[`pvc_${inch}_total`] > 0 ? `₹${data.summary[`pvc_${inch}_total`].toLocaleString()}` : '-'}
                  </td>
                ))}
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2 text-sm text-right text-blue-600">{data.summary.total_rpm.toFixed(1)}</td>
                <td className="px-3 py-2 text-sm text-right text-gray-900">₹{data.summary.total_bill_amount.toLocaleString()}</td>
                <td className="px-3 py-2 text-sm text-right text-green-600">₹{data.summary.total_received_amount.toLocaleString()}</td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2 text-sm text-right text-orange-600">₹{data.summary.total_pipe_cost.toLocaleString()}</td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Reports;
